import { useMemo, useState } from 'react';
import type { ColumnMapping, UploadResponse } from '../../lib/trpc';
import { CANONICAL_FIELDS_UI } from '../../lib/canonical';
import { Tooltip } from '../Tooltip';
import { TOOLTIPS, type TooltipKey } from '../../lib/tooltips';

// Orientação por tipo de pendência (as demais não têm tooltip específico).
const PENDENCIA_TIP: Record<string, TooltipKey> = {
  sem_contato: 'revisaoContato',
  saldo_estimado: 'revisaoSaldo',
};

const FORMATO_LABEL: Record<string, string> = {
  wide: 'Parcelas em colunas (uma linha por contrato)',
  long: 'Uma linha por parcela',
  single: 'Uma parcela por contrato',
  unknown: 'Formato de parcela não identificado',
};

function confChip(conf: number) {
  const color = conf >= 0.8 ? 'var(--accent-bright)' : conf >= 0.5 ? 'var(--ink-2)' : 'var(--risk)';
  return (
    <span className="num text-xs" style={{ color }}>
      {Math.round(conf * 100)}%
    </span>
  );
}

/**
 * Tela de revisão (crua, funcional): mostra o mapeamento inferido por coluna,
 * permite corrigir, lista pendências e o formato detectado. Ao confirmar, envia
 * as correções e grava os dados.
 */
export function ReviewScreen({
  res,
  onConfirm,
  onCancel,
}: {
  res: UploadResponse;
  onConfirm: (correcoes: ColumnMapping[]) => Promise<void>;
  onCancel: () => void;
}) {
  // Estado editável: mapa coluna → campo canônico (ou null).
  const inicial = useMemo(() => {
    const m = new Map<string, ColumnMapping>();
    for (const h of res.headers) m.set(h, { coluna: h, campo: null, confianca: 0 });
    for (const mp of res.mapeamento) m.set(mp.coluna, mp);
    return m;
  }, [res]);

  const [mapa, setMapa] = useState<Map<string, ColumnMapping>>(inicial);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function setCampo(coluna: string, campo: string | null) {
    setMapa((prev) => {
      const next = new Map(prev);
      const cur = next.get(coluna)!;
      next.set(coluna, { ...cur, campo, confianca: campo === cur.campo ? cur.confianca : 1 });
      return next;
    });
  }

  const mapeamento = [...mapa.values()];
  const mapeados = new Set(mapeamento.map((m) => m.campo).filter(Boolean));
  const obrigatoriosFaltando = CANONICAL_FIELDS_UI.filter((f) => f.required && !mapeados.has(f.key));

  async function confirmar() {
    setErro(null);
    setEnviando(true);
    try {
      await onConfirm(mapeamento);
    } catch (e) {
      setErro((e as Error).message ?? 'Falha ao gravar.');
      setEnviando(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Resumo do que foi detectado */}
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-ink">{res.nomeArquivo}</div>
            <div className="mt-1 text-xs text-ink-3">
              Formato detectado: {FORMATO_LABEL[res.formato.format] ?? res.formato.format}
            </div>
          </div>
          <div className="flex gap-4 text-center">
            <Stat n={res.resumo.contratos} l="contratos" />
            <Stat n={res.resumo.parcelas} l="parcelas" />
            <Stat n={res.pendencias.length} l="pendências" risco={res.pendencias.length > 0} />
          </div>
        </div>
      </div>

      {/* Resumo incremental: o que a importação vai fazer na carteira ATUAL */}
      <ResumoIncremental inc={res.incremental} />

      {/* Editor de mapeamento */}
      <div className="card overflow-hidden">
        <div className="border-b border-line px-5 py-3 text-sm font-medium text-ink-2">
          Mapeamento das colunas
        </div>
        {/* overflow-x-auto + min-w no table: no mobile rola na horizontal em vez
            de espremer as colunas até ficarem ilegíveis. */}
        <div className="max-h-[360px] overflow-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="sticky top-0 bg-surface-2 text-left text-xs uppercase tracking-wide text-ink-4">
              <tr>
                <th className="px-5 py-2 font-medium">Coluna do arquivo</th>
                <th className="px-3 py-2 font-medium">Confiança</th>
                <th className="px-5 py-2 font-medium">Campo no AMA Capital</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {mapeamento.map((m) => (
                <tr key={m.coluna}>
                  <td className="px-5 py-2 text-ink">{m.coluna}</td>
                  <td className="px-3 py-2">{m.campo ? confChip(m.confianca) : <span className="text-ink-4">-</span>}</td>
                  <td className="px-5 py-2">
                    <select
                      value={m.campo ?? ''}
                      onChange={(e) => setCampo(m.coluna, e.target.value || null)}
                      className="w-full rounded-sm border border-line-2 bg-surface px-2 py-2 text-sm text-ink outline-none focus:border-accent"
                    >
                      <option value="">ignorar coluna</option>
                      {CANONICAL_FIELDS_UI.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                          {f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pendências */}
      {res.pendencias.length > 0 && (
        <div className="card p-5">
          <div className="mb-2 text-sm font-medium text-ink-2">
            Pendências detectadas ({res.pendencias.length})
          </div>
          <ul className="max-h-44 space-y-1 overflow-auto">
            {res.pendencias.slice(0, 50).map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: 'var(--risk)' }} />
                <span className="inline-flex items-center gap-1">
                  {p.mensagem}
                  {p.linha ? <span className="text-ink-4"> (linha {p.linha})</span> : null}
                  {PENDENCIA_TIP[p.tipo] && <Tooltip text={TOOLTIPS[PENDENCIA_TIP[p.tipo]!]} label="O que é esta pendência" />}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {obrigatoriosFaltando.length > 0 && (
        <div
          className="rounded-sm border px-4 py-3 text-sm"
          style={{ borderColor: 'var(--risk)', background: 'var(--risk-soft)', color: 'var(--ink-2)' }}
        >
          Faltam campos obrigatórios: {obrigatoriosFaltando.map((f) => f.label).join(', ')}. Ajuste o
          mapeamento antes de confirmar.
        </div>
      )}

      {erro && <div className="text-sm text-risk">{erro}</div>}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button className="btn btn-ghost w-full sm:w-auto" onClick={onCancel} disabled={enviando}>
          Cancelar
        </button>
        <button
          className="btn btn-primary w-full sm:w-auto"
          onClick={confirmar}
          disabled={enviando || obrigatoriosFaltando.length > 0}
        >
          {enviando ? 'Gravando…' : 'Confirmar e gravar'}
        </button>
      </div>
    </div>
  );
}

function Stat({ n, l, risco }: { n: number; l: string; risco?: boolean }) {
  return (
    <div>
      <div className="num text-xl font-semibold" style={{ color: risco ? 'var(--risk)' : 'var(--ink)' }}>
        {n}
      </div>
      <div className="text-xs text-ink-3">{l}</div>
    </div>
  );
}

/**
 * Resumo da RE-IMPORTAÇÃO INCREMENTAL, mostrado ANTES de confirmar: quantos
 * contratos são novos, atualizados, sem mudança, ausentes (na base mas fora da
 * planilha) e quantas divergências precisam de revisão. Nunca deleta nada.
 */
export function ResumoIncremental({ inc }: { inc: import('../../lib/trpc').IncrementalResumo }) {
  const detalheAtualizados =
    inc.parcelasPagas + inc.parcelasNovas + inc.contatosPreenchidos > 0
      ? [
          inc.parcelasPagas > 0 ? `${inc.parcelasPagas} parcela${inc.parcelasPagas > 1 ? 's' : ''} paga${inc.parcelasPagas > 1 ? 's' : ''}` : null,
          inc.parcelasNovas > 0 ? `${inc.parcelasNovas} parcela${inc.parcelasNovas > 1 ? 's' : ''} nova${inc.parcelasNovas > 1 ? 's' : ''}` : null,
          inc.contatosPreenchidos > 0 ? `${inc.contatosPreenchidos} contato${inc.contatosPreenchidos > 1 ? 's' : ''} preenchido${inc.contatosPreenchidos > 1 ? 's' : ''}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <div className="card p-5" data-testid="review-incremental">
      <div className="mb-3 text-sm font-medium text-ink-2">O que esta importação vai fazer</div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <ChipIncremental n={inc.novos} l="novos" cor="var(--accent-bright)" />
        <ChipIncremental n={inc.atualizados} l="atualizados" cor="var(--accent-bright)" sub={detalheAtualizados} />
        <ChipIncremental n={inc.semMudanca} l="sem mudança" cor="var(--ink-3)" />
        <ChipIncremental n={inc.ausentes} l="na base, fora da planilha" cor="var(--ink-3)" />
        <ChipIncremental n={inc.divergencias} l="divergências p/ revisar" cor="var(--risk)" risco={inc.divergencias > 0} />
      </div>

      {inc.divergencias > 0 && (
        <div
          className="mt-4 rounded-sm border px-4 py-3 text-sm"
          style={{ borderColor: 'var(--risk)', background: 'var(--risk-soft)', color: 'var(--ink-2)' }}
          data-testid="review-divergencias"
        >
          <div className="mb-1 font-medium">Divergências para você decidir (não serão sobrescritas):</div>
          <ul className="space-y-1">
            {inc.divergenciasLista.slice(0, 20).map((d, i) => (
              <li key={i}>• {d.mensagem}</li>
            ))}
          </ul>
        </div>
      )}

      {inc.ausentes > 0 && (
        <div className="mt-3 text-xs text-ink-3">
          {inc.ausentes} contrato{inc.ausentes > 1 ? 's' : ''} da base {inc.ausentes > 1 ? 'não vieram' : 'não veio'} nesta
          planilha. Nada é apagado — re-importação nunca deleta contrato.
        </div>
      )}
    </div>
  );
}

function ChipIncremental({ n, l, cor, sub, risco }: { n: number; l: string; cor: string; sub?: string | null; risco?: boolean }) {
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className="num text-2xl font-semibold" style={{ color: risco || n > 0 ? cor : 'var(--ink-4)' }}>
          {n}
        </span>
        <span className="text-sm text-ink-3">{l}</span>
      </div>
      {sub && <div className="mt-0.5 text-xs text-ink-4">{sub}</div>}
    </div>
  );
}
